import { useEffect, useMemo, useState } from "react";
import {
  Download, Filter, RefreshCw, HeartPulse, Trash2, Check, X, Paperclip,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { api, apiErrorMessage, getToken, API, formatTanggalID, formatRupiah, openStoredFile } from "@/lib/api";

const ANY = "__ANY__";
const STATUS_OPTIONS = ["Menunggu", "Disetujui", "Ditolak"];

function toDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminLaporanKlaim() {
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toDateInput(first));
  const [endDate, setEndDate] = useState(toDateInput(today));
  const [unitId, setUnitId] = useState(ANY);
  const [kategoriId, setKategoriId] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [units, setUnits] = useState([]);
  const [kategori, setKategori] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, k] = await Promise.all([
          api.get("/master/unit"),
          api.get("/klaim/kategori"),
        ]);
        setUnits(u.data);
        setKategori(k.data);
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
    })();
  }, []);

  const params = useMemo(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      unit_id: unitId !== ANY ? unitId : undefined,
      kategori_id: kategoriId !== ANY ? kategoriId : undefined,
      status: status !== ANY ? status : undefined,
    }),
    [startDate, endDate, unitId, kategoriId, status]
  );

  async function loadReport() {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/klaim", { params });
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function refreshBadge() {
    window.dispatchEvent(new Event("atlas:refresh-approvals"));
  }

  async function doExport(kind) {
    setExporting(kind);
    try {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") search.append(k, v);
      });
      const ext = kind === "csv" ? "export.csv" : "export";
      const url = `${API}/reports/klaim/${ext}?${search.toString()}`;
      const token = getToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal export");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `laporan-klaim-${startDate}-${endDate}.${kind === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Laporan berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal export");
    } finally {
      setExporting(null);
    }
  }

  async function approveRow(r) {
    if (!window.confirm(`Setujui klaim ${r.kategori_nama} ${formatRupiah(r.nilai)} dari ${r.nama}?`)) return;
    setWorking(true);
    try {
      await api.post(`/klaim/${r.id}/approve`);
      toast.success("Klaim disetujui — saldo plafon terpotong");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function submitReject() {
    if (rejectReason.trim().length < 3) {
      toast.error("Alasan penolakan wajib diisi (min. 3 karakter)");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/klaim/${rejectTarget.id}/reject`, { alasan: rejectReason.trim() });
      toast.success("Klaim ditolak");
      setRejectTarget(null);
      setRejectReason("");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function del(r) {
    if (!window.confirm(`Hapus klaim ${r.kategori_nama} ${formatRupiah(r.nilai)} dari ${r.nama}?`)) return;
    try {
      await api.delete(`/klaim/${r.id}`);
      toast.success("Dihapus");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  const stat = useMemo(() => {
    const total = rows.length;
    const setuju = rows.filter((r) => r.status === "Disetujui");
    const tolak = rows.filter((r) => r.status === "Ditolak").length;
    const menunggu = rows.filter((r) => r.status === "Menunggu").length;
    const totalNilai = setuju.reduce((a, r) => a + (r.nilai || 0), 0);
    return { total, setuju: setuju.length, tolak, menunggu, totalNilai };
  }, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-laporan-klaim">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Laporan Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Laporan Klaim Pengobatan
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Semua klaim reimbursement lintas unit. Plafon &amp; kategori diatur di menu Konfigurasi Kebijakan.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadReport} disabled={loading} data-testid="klaim-refresh-btn" className="gap-2">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          <Button
            onClick={() => doExport("xlsx")}
            disabled={exporting !== null || rows.length === 0}
            className="gap-2 bg-teal-800 hover:bg-teal-900"
            data-testid="klaim-export-xlsx-btn"
          >
            <Download size={14} /> {exporting === "xlsx" ? "Menyiapkan…" : "Export Excel"}
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
          <Filter size={12} /> Filter Unit &amp; Periode
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dari</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5" data-testid="klaim-start-date" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sampai</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5" data-testid="klaim-end-date" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit</Label>
            <Select value={unitId} onValueChange={setUnitId}>
              <SelectTrigger className="mt-1.5" data-testid="klaim-filter-unit"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Unit</SelectItem>
                {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Kategori</Label>
            <Select value={kategoriId} onValueChange={setKategoriId}>
              <SelectTrigger className="mt-1.5" data-testid="klaim-filter-kategori"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Kategori</SelectItem>
                {kategori.map((k) => (<SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1.5" data-testid="klaim-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Semua Status</SelectItem>
                {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={loadReport} className="bg-teal-700 hover:bg-teal-800" data-testid="klaim-apply-filter-btn">
            Terapkan Filter
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBox label="Total Klaim" value={stat.total} tone="slate" testId="stat-klaim-total" />
        <StatBox label="Menunggu" value={stat.menunggu} tone="amber" testId="stat-klaim-menunggu" />
        <StatBox label="Disetujui" value={stat.setuju} tone="emerald" testId="stat-klaim-setuju" />
        <StatBox label="Nilai Disetujui" value={formatRupiah(stat.totalNilai)} tone="teal" small testId="stat-klaim-nilai" />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0">
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit / Jabatan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Kategori</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Nilai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Bukti</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <HeartPulse size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada data untuk filter ini</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`klaim-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-800 whitespace-nowrap">
                      {formatTanggalID(r.tanggal)}
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-xs">
                      <div>{r.unit_nama}</div>
                      <div className="text-slate-400">{r.jabatan_nama || "—"}</div>
                    </TableCell>
                    <TableCell className="text-slate-800 text-sm font-semibold whitespace-nowrap">
                      {r.kategori_nama}
                    </TableCell>
                    <TableCell className="font-mono-clock text-teal-800 font-bold whitespace-nowrap text-right">
                      {formatRupiah(r.nilai)}
                    </TableCell>
                    <TableCell>
                      {r.lampiran_path ? (
                        <button
                          onClick={() => openStoredFile(r.lampiran_path).catch(() => toast.error("Gagal membuka lampiran"))}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-teal-800 hover:text-teal-900"
                          data-testid={`klaim-bukti-${r.id}`}
                        >
                          <Paperclip size={12} /> Lihat
                        </button>
                      ) : "—"}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} size="sm" /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {r.status === "Menunggu" ? (
                        <>
                          <button
                            onClick={() => approveRow(r)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 mr-3"
                            data-testid={`klaim-approve-${r.id}`}
                          >
                            <Check size={13} /> Setujui
                          </button>
                          <button
                            onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 mr-3"
                            data-testid={`klaim-reject-${r.id}`}
                          >
                            <X size={13} /> Tolak
                          </button>
                        </>
                      ) : null}
                      {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="max-w-md" data-testid="klaim-reject-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Klaim</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `${rejectTarget.nama} — ${rejectTarget.kategori_nama} ${formatRupiah(rejectTarget.nilai)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Penolakan (wajib)
            </Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="input-klaim-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Batal</Button>
            <Button onClick={submitReject} disabled={working} className="bg-rose-600 hover:bg-rose-700" data-testid="klaim-reject-confirm-btn">
              {working ? "Memproses…" : "Tolak Klaim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone, small, testId }) {
  const toneMap = {
    slate: "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800",
    rose: "border-rose-200 text-rose-800",
    amber: "border-amber-200 text-amber-800",
    teal: "border-teal-200 text-teal-800",
  };
  return (
    <div className={`rounded-xl border bg-white p-5 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
        {label}
      </div>
      <div className={`mt-1 font-mono-clock font-bold leading-tight ${small ? "text-xl" : "text-3xl"}`}>{value}</div>
    </div>
  );
}
